import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { CdkTextareaAutosize } from '@angular/cdk/text-field';
import { ConfigService } from 'src/app/services/config.service';
import { KeycloakService } from 'keycloak-angular';

export interface ChatInputSubmitEvent {
  content: string;
  imageUrl?: string;
  inputModality?: 'voice' | 'text';
}

export interface PendingFile {
  file: File;
  name: string;
  size: number;
  isImage: boolean;
  previewUrl?: string;
}

/**
 * Isolated chat input component with OnPush change detection.
 * This component owns all input-related logic including voice recording and image handling.
 * OnPush ensures typing doesn't trigger change detection in the parent's heavy chat-screen.
 */
@Component({
    selector: 'app-chat-input',
    templateUrl: './chat-input.component.html',
    styleUrls: ['./chat-input.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ChatInputComponent implements OnInit, AfterViewInit, OnDestroy, OnChanges {
  @ViewChild('chatInput') chatInput!: ElementRef<HTMLTextAreaElement>;
  @ViewChild('promptBox') promptBox!: ElementRef<HTMLDivElement>;
  @ViewChild(CdkTextareaAutosize) autosize!: CdkTextareaAutosize;

  @Input() disabled: boolean = false;
  @Input() loading: boolean = false;
  @Input() agentsLoaded: boolean = true;
  @Input() slashCommandActive: boolean = false;
  @Input() chatActive: boolean = false;
  @Input() safeMode: boolean = false;
  @Input() showSafeMode: boolean = true;
  @Input() channelId: string | null = null;
  @Input() team: string | null = null;
  @Input() placeholder: string = '';
  @Input() showAmbientMic: boolean = false;
  @Input() ambientActive: boolean = false;
  @Input() cancelState: 'none' | 'interrupt' | 'cancel' = 'none';

  @Output() submit = new EventEmitter<ChatInputSubmitEvent>();
  @Output() cancelSlashCommand = new EventEmitter<void>();
  @Output() cancelChat = new EventEmitter<void>();
  @Output() toggleSafeMode = new EventEmitter<void>();
  @Output() toggleAmbient = new EventEmitter<void>();
  @Output() inputResized = new EventEmitter<void>();

  // Internal state
  public input: string = '';
  public isInputFocused: boolean = false;
  public isRecording: boolean = false;
  public speechRecognitionSupported: boolean = false;
  public pendingImages: string[] = [];  // data URLs for preview (up to 3)
  private pendingImageFiles: File[] = [];  // File objects for upload
  public pendingFiles: PendingFile[] = [];
  public isDragging: boolean = false;
  private dragCounter: number = 0;
  private readonly MAX_FILE_SIZE = 25 * 1024 * 1024; // 25MB per file

  // Voice recording
  private speechRecognition: any = null;
  private textBeforeRecording: string = '';
  private voiceHoldTimeout: any = null;
  private readonly originalPlaceholder = 'Message Lit';

  // ResizeObserver for textarea auto-scroll
  private textareaResizeObserver: ResizeObserver | null = null;

  constructor(private cdr: ChangeDetectorRef, private ngZone: NgZone, private keycloak: KeycloakService) {}

  private async getAuthToken(): Promise<string> {
    if (ConfigService.localMode) return '';
    try {
      await this.keycloak.updateToken(30);
      return await this.keycloak.getToken() || '';
    } catch (e) {
      return '';
    }
  }

  public get isMobileDevice(): boolean {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  }

  public get submitDisabled(): boolean {
    const hasContent = (this.input && this.input.trim().length > 0) || this.pendingImages.length > 0 || this.pendingFiles.length > 0;
    return !hasContent || this.loading || !this.agentsLoaded;
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Re-focus textarea when loading transitions false → true → false (e.g. channel send)
    if (changes['loading'] && !changes['loading'].currentValue && changes['loading'].previousValue) {
      setTimeout(() => this.chatInput?.nativeElement?.focus(), 0);
    }
  }

  ngOnInit(): void {
    this.initSpeechRecognition();
  }

  ngAfterViewInit(): void {
    // Setup after view is ready
    setTimeout(() => {
      this.setupPushToTalk();
      this.setupTextareaResizeObserver();
      this.setupInputOutsideZone();
    }, 0);
  }

  /**
   * Set up input handling outside Angular zone to prevent CD on every keystroke.
   * We read value directly from textarea, no [value] binding needed.
   */
  private setupInputOutsideZone(): void {
    const textarea = this.chatInput?.nativeElement;
    if (!textarea) return;

    this.ngZone.runOutsideAngular(() => {
      // Handle input changes outside zone - just sync our property and trigger autosize
      textarea.addEventListener('input', () => {
        this.input = textarea.value;
        // Manually trigger autosize since input events are unpatched from zone
        this.autosize?.resizeToFitContent(true);
        // Scroll cursor into view after autosize - only if cursor is at the end
        // This keeps typing visible without jumping when editing earlier text
        requestAnimationFrame(() => {
          if (textarea.selectionStart === textarea.value.length) {
            textarea.scrollTop = textarea.scrollHeight;
          }
        });
      });

      // Handle keydown outside zone, only enter zone for Enter key
      textarea.addEventListener('keydown', (event: KeyboardEvent) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          this.input = textarea.value; // Sync before submit
          this.ngZone.run(() => {
            if (event.shiftKey) {
              this.insertNewline();
            } else {
              this.$submit();
            }
          });
        }
      });

      // Handle focus/blur outside zone (only used for mobile placeholder)
      textarea.addEventListener('focus', () => {
        this.isInputFocused = true;
      });
      textarea.addEventListener('blur', () => {
        this.isInputFocused = false;
      });
    });
  }

  ngOnDestroy(): void {
    if (this.voiceHoldTimeout) {
      clearTimeout(this.voiceHoldTimeout);
    }
    if (this.textareaResizeObserver) {
      this.textareaResizeObserver.disconnect();
    }
    if (this.speechRecognition) {
      try {
        this.speechRecognition.stop();
      } catch (e) {
        // Ignore
      }
    }
  }

  /**
   * Handle keydown events
   */
  public $keydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && event.shiftKey) {
      event.preventDefault();
      this.insertNewline();
    } else if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.$submit();
    }
  }

  /**
   * Insert newline at cursor position
   */
  private insertNewline(): void {
    const textarea = this.chatInput?.nativeElement;
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const value = textarea.value || '';
      const newValue = value.substring(0, start) + '\n' + value.substring(end);
      textarea.value = newValue;
      this.input = newValue;
      textarea.selectionStart = textarea.selectionEnd = start + 1;
    }
  }

  /**
   * Submit the current input
   */
  public async $submit(): Promise<void> {
    if (this.submitDisabled) return;

    let content = this.input;
    let imageUrl: string | undefined;

    // Upload all pending images (up to 3)
    if (this.pendingImageFiles.length > 0) {
      const muxUrl = ConfigService.muxUrl.replace(/\/api$/, '');
      const imageMarkdowns: string[] = [];
      const imageNames: string[] = [];
      const failedImages: string[] = [];

      for (const file of this.pendingImageFiles) {
        try {
          const formData = new FormData();
          formData.append('file', file);
          const uploadUrl = this.channelId
            ? `${muxUrl}/api/upload?channel_id=${encodeURIComponent(this.channelId)}${this.team ? '&team=' + encodeURIComponent(this.team) : ''}`
            : `${muxUrl}/api/upload`;
          const token = await this.getAuthToken();
          const headers: Record<string, string> = {};
          if (token) headers['Authorization'] = `Bearer ${token}`;
          const resp = await fetch(uploadUrl, {
            method: 'POST',
            headers,
            body: formData
          });
          if (resp.ok) {
            const data = await resp.json();
            if (data.success) {
              const resolvedUrl = `${muxUrl}${data.url}`;
              if (!imageUrl) imageUrl = resolvedUrl;
              imageMarkdowns.push(`![Pasted image](${resolvedUrl})`);
              imageNames.push(data.filename);
            } else {
              failedImages.push(file.name || 'image');
            }
          } else {
            failedImages.push(file.name || 'image');
          }
        } catch (err) {
          console.error('Failed to upload pasted image:', err);
          failedImages.push(file.name || 'image');
        }
      }

      if (imageMarkdowns.length > 0) {
        const imagesBlock = imageMarkdowns.join('\n\n');
        const namesNote = imageNames.map(n => `(Image: ${n})`).join(' ');
        content = content
          ? `${imagesBlock}\n\n${content}\n\n${namesNote}`
          : `${imagesBlock}\n\nPlease look at ${imageNames.length > 1 ? 'these images' : 'this image'}: ${namesNote}`;
      }
      if (failedImages.length > 0) {
        const failNotice = `> **Image upload failed:** ${failedImages.join(', ')}`;
        content = content ? `${content}\n\n${failNotice}` : failNotice;
      }

      this.pendingImages = [];
      this.pendingImageFiles = [];
    }

    // Upload pending non-image files
    if (this.pendingFiles.length > 0) {
      const muxUrl = ConfigService.muxUrl.replace(/\/api$/, '');
      const fileMarkdowns: string[] = [];
      const failedFiles: string[] = [];

      for (const pf of this.pendingFiles) {
        try {
          const formData = new FormData();
          formData.append('file', pf.file);
          const uploadUrl = this.channelId
            ? `${muxUrl}/api/upload?channel_id=${encodeURIComponent(this.channelId)}${this.team ? '&team=' + encodeURIComponent(this.team) : ''}`
            : `${muxUrl}/api/upload`;
          const token = await this.getAuthToken();
          const headers: Record<string, string> = {};
          if (token) headers['Authorization'] = `Bearer ${token}`;
          const resp = await fetch(uploadUrl, {
            method: 'POST',
            headers,
            body: formData
          });
          if (resp.ok) {
            const data = await resp.json();
            if (data.success) {
              const resolvedUrl = `${muxUrl}${data.url}`;
              fileMarkdowns.push(`[${data.original_name || pf.name}](${resolvedUrl})`);
            } else {
              failedFiles.push(pf.name);
            }
          } else {
            let reason = '';
            try { const err = await resp.json(); reason = err.detail || ''; } catch {}
            failedFiles.push(reason ? `${pf.name} (${reason})` : pf.name);
          }
        } catch (err) {
          console.error('Failed to upload file:', pf.name, err);
          failedFiles.push(pf.name);
        }
      }

      if (fileMarkdowns.length > 0) {
        const filesBlock = fileMarkdowns.join('\n\n');
        content = content
          ? `${content}\n\n${filesBlock}`
          : `${filesBlock}\n\nPlease review ${fileMarkdowns.length > 1 ? 'these files' : 'this file'}.`;
      }
      if (failedFiles.length > 0) {
        const failNotice = failedFiles.map(f => `- ${f}`).join('\n');
        content = content
          ? `${content}\n\n> **Upload failed:**\n${failNotice}`
          : `> **Upload failed:**\n${failNotice}`;
      }

      this.pendingFiles = [];
    }

    // Emit and clear
    this.submit.emit({ content, imageUrl });
    this.input = '';
    if (this.chatInput?.nativeElement) {
      this.chatInput.nativeElement.value = '';
      this.chatInput.nativeElement.focus();
    }
    this.cdr.detectChanges();
  }

  /**
   * Handle photo capture or image upload (auto-sends immediately)
   */
  public async $onPhotoCapture(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    input.value = '';  // Reset so same file can be selected again

    const formData = new FormData();
    formData.append('file', file);

    try {
      const muxUrl = ConfigService.muxUrl.replace(/\/api$/, '');
      const uploadUrl = this.channelId
        ? `${muxUrl}/api/upload?channel_id=${encodeURIComponent(this.channelId)}${this.team ? '&team=' + encodeURIComponent(this.team) : ''}`
        : `${muxUrl}/api/upload`;
      const token = await this.getAuthToken();
      const headers: Record<string, string> = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const resp = await fetch(uploadUrl, {
        method: 'POST',
        headers,
        body: formData
      });

      if (!resp.ok) {
        console.error('Upload failed:', resp.status, resp.statusText);
        return;
      }

      const data = await resp.json();
      if (data.success) {
        const imageUrl = `${muxUrl}${data.url}`;
        const imageMarkdown = `![Uploaded image](${imageUrl})`;
        this.submit.emit({
          content: `${imageMarkdown}\n\nPlease look at this image: ${data.filename}`,
          imageUrl
        });
      } else {
        console.error('Upload failed:', data.error);
      }
    } catch (err) {
      console.error('Upload error:', err);
    }
  }

  /**
   * Handle clipboard paste for images
   */
  @HostListener('paste', ['$event'])
  public onPaste(event: ClipboardEvent): void {
    const items = event.clipboardData?.items;
    if (!items) return;

    const MAX_IMAGES = 3;
    for (let i = 0; i < items.length; i++) {
      if (this.pendingImageFiles.length >= MAX_IMAGES) break;
      const item = items[i];
      if (item.type.startsWith('image/')) {
        event.preventDefault();
        const file = item.getAsFile();
        if (!file) continue;

        this.pendingImageFiles.push(file);
        const reader = new FileReader();
        reader.onload = () => {
          this.pendingImages.push(reader.result as string);
          this.cdr.detectChanges();
        };
        reader.readAsDataURL(file);
      }
    }
  }

  @HostListener('dragenter', ['$event'])
  public onDragEnter(event: DragEvent): void {
    if (!event.dataTransfer?.types?.includes('Files')) return;
    event.preventDefault();
    this.dragCounter++;
    this.isDragging = true;
    this.cdr.detectChanges();
  }

  @HostListener('dragover', ['$event'])
  public onDragOver(event: DragEvent): void {
    if (!event.dataTransfer?.types?.includes('Files')) return;
    event.preventDefault();
    event.dataTransfer!.dropEffect = 'copy';
  }

  @HostListener('dragleave', ['$event'])
  public onDragLeave(event: DragEvent): void {
    this.dragCounter--;
    if (this.dragCounter <= 0) {
      this.dragCounter = 0;
      this.isDragging = false;
      this.cdr.detectChanges();
    }
  }

  @HostListener('drop', ['$event'])
  public onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragCounter = 0;
    this.isDragging = false;

    const files = event.dataTransfer?.files;
    if (!files || files.length === 0) return;

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (file.size > this.MAX_FILE_SIZE) {
        console.warn(`File "${file.name}" exceeds 25MB limit, skipping`);
        continue;
      }
      const isImage = file.type.startsWith('image/');
      if (isImage && this.pendingImageFiles.length < 3) {
        this.pendingImageFiles.push(file);
        const reader = new FileReader();
        reader.onload = () => {
          this.pendingImages.push(reader.result as string);
          this.cdr.detectChanges();
        };
        reader.readAsDataURL(file);
      } else {
        this.pendingFiles.push({
          file,
          name: file.name,
          size: file.size,
          isImage: false,
        });
      }
    }
    this.cdr.detectChanges();
  }

  public $onFileAttach(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files) return;
    for (let i = 0; i < input.files.length; i++) {
      const file = input.files[i];
      if (file.size > this.MAX_FILE_SIZE) continue;
      if (file.type.startsWith('image/')) {
        if (this.pendingImageFiles.length < 3) {
          this.pendingImageFiles.push(file);
          const reader = new FileReader();
          reader.onload = () => {
            this.pendingImages.push(reader.result as string);
            this.cdr.detectChanges();
          };
          reader.readAsDataURL(file);
        }
      } else {
        this.pendingFiles.push({ file, name: file.name, size: file.size, isImage: false });
      }
    }
    input.value = '';
    this.cdr.detectChanges();
  }

  public removePendingFile(index: number): void {
    this.pendingFiles.splice(index, 1);
    this.cdr.detectChanges();
  }

  public formatFileSize(bytes: number): string {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(0) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  }

  /**
   * Clear pending pasted image
   */
  public $clearPendingImage(index?: number): void {
    if (index !== undefined) {
      this.pendingImages.splice(index, 1);
      this.pendingImageFiles.splice(index, 1);
    } else {
      this.pendingImages = [];
      this.pendingImageFiles = [];
    }
    this.cdr.detectChanges();
  }

  /**
   * Initialize speech recognition
   */
  private initSpeechRecognition(): void {
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    this.speechRecognitionSupported = !!SpeechRecognition;
  }

  /**
   * Setup push-to-talk on the prompt box
   */
  private setupPushToTalk(): void {
    const textarea = this.chatInput?.nativeElement;
    const promptBox = this.promptBox?.nativeElement;
    if (!textarea || !promptBox || !this.speechRecognitionSupported) return;

    const HOLD_THRESHOLD = 300;

    const startHold = (e: Event) => {
      const target = e.target as HTMLElement;
      if (target.tagName === 'BUTTON' || target.closest('button') ||
          target.tagName === 'LABEL' || target.closest('label') ||
          target.tagName === 'MAT-ICON' || target.closest('mat-icon')) {
        return;
      }

      if (this.voiceHoldTimeout) {
        clearTimeout(this.voiceHoldTimeout);
      }

      this.voiceHoldTimeout = setTimeout(() => {
        window.getSelection()?.removeAllRanges();
        textarea.style.setProperty('user-select', 'none', 'important');
        textarea.placeholder = 'Listening... Release to stop';
        this.$startVoiceRecording();
      }, HOLD_THRESHOLD);
    };

    const endHold = () => {
      if (this.voiceHoldTimeout) {
        clearTimeout(this.voiceHoldTimeout);
        this.voiceHoldTimeout = null;
      }
      if (this.isRecording) {
        this.$stopVoiceRecording();
        textarea.style.removeProperty('user-select');
        textarea.placeholder = this.originalPlaceholder;
      }
    };

    const cancelHold = () => {
      if (this.voiceHoldTimeout) {
        clearTimeout(this.voiceHoldTimeout);
        this.voiceHoldTimeout = null;
      }
      if (this.isRecording) {
        this.$stopVoiceRecording();
        textarea.style.removeProperty('user-select');
        textarea.placeholder = this.originalPlaceholder;
      }
    };

    // Touch events
    promptBox.addEventListener('touchstart', startHold);
    promptBox.addEventListener('touchend', endHold);
    promptBox.addEventListener('touchcancel', cancelHold);

    // Mouse events (only when empty)
    promptBox.addEventListener('mousedown', (e: MouseEvent) => {
      const isEmpty = !this.input || this.input.trim() === '';
      if (e.button === 0 && isEmpty) {
        startHold(e);
      }
    }, { capture: true });
    promptBox.addEventListener('mouseup', endHold, { capture: true });
  }

  /**
   * Setup ResizeObserver to handle textarea expansion
   */
  private setupTextareaResizeObserver(): void {
    const textarea = this.chatInput?.nativeElement;
    if (!textarea) return;

    this.textareaResizeObserver = new ResizeObserver(() => {
      // Emit event so parent can scroll chat to bottom
      this.inputResized.emit();
    });
    this.textareaResizeObserver.observe(textarea);
  }

  /**
   * Handle touch start on voice button
   */
  public $onVoiceTouchStart(event: TouchEvent): void {
    event.preventDefault();
    if (this.chatInput?.nativeElement) {
      this.chatInput.nativeElement.blur();
    }
    this.isInputFocused = false;
    this.$startVoiceRecording();
  }

  /**
   * Start voice recording
   */
  public $startVoiceRecording(): void {
    if (this.isRecording) return;

    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRecognition) return;

    this.speechRecognition = new SpeechRecognition();
    this.speechRecognition.continuous = true;
    this.speechRecognition.interimResults = true;
    this.speechRecognition.lang = 'en-US';

    this.textBeforeRecording = this.input || '';
    this.isRecording = true;
    this.cdr.detectChanges();

    // Global listeners to catch release even if cursor moved
    const globalMouseUpHandler = () => {
      window.removeEventListener('mouseup', globalMouseUpHandler);
      this.$stopVoiceRecording();
    };
    window.addEventListener('mouseup', globalMouseUpHandler);

    const globalTouchEndHandler = () => {
      window.removeEventListener('touchend', globalTouchEndHandler);
      window.removeEventListener('touchcancel', globalTouchEndHandler);
      this.$stopVoiceRecording();
    };
    window.addEventListener('touchend', globalTouchEndHandler);
    window.addEventListener('touchcancel', globalTouchEndHandler);

    this.speechRecognition.onresult = (event: any) => {
      const lastResult = event.results[event.results.length - 1];
      const transcript = lastResult[0].transcript;
      this.input = (this.textBeforeRecording || '') + transcript;
      if (this.chatInput?.nativeElement) {
        this.chatInput.nativeElement.value = this.input;
      }
      this.cdr.detectChanges();
    };

    this.speechRecognition.onerror = (event: any) => {
      if (event.error === 'no-speech') return;
      console.error('[Voice] Error:', event.error);
      this.isRecording = false;
      this.cdr.detectChanges();
    };

    this.speechRecognition.onend = () => {
      if (this.isRecording) {
        try {
          this.textBeforeRecording = this.input;
          this.speechRecognition.start();
        } catch (e) {
          this.isRecording = false;
          this.cdr.detectChanges();
        }
      }
    };

    try {
      this.speechRecognition.start();
    } catch (e) {
      console.error('Failed to start voice recording:', e);
      this.isRecording = false;
      this.cdr.detectChanges();
    }
  }

  /**
   * Stop voice recording
   */
  public $stopVoiceRecording(): void {
    if (!this.isRecording || !this.speechRecognition) return;

    this.isRecording = false;
    try {
      this.speechRecognition.stop();
    } catch (e) {
      // Ignore
    }
    this.cdr.detectChanges();
  }

  // Public methods for parent to call
  public focus(): void {
    this.chatInput?.nativeElement?.focus();
  }

  public setInput(value: string): void {
    this.input = value;
    if (this.chatInput?.nativeElement) {
      this.chatInput.nativeElement.value = value;
    }
    this.cdr.detectChanges();
  }

  public getInput(): string {
    return this.input;
  }

  public clearInput(): void {
    this.input = '';
    if (this.chatInput?.nativeElement) {
      this.chatInput.nativeElement.value = '';
    }
    this.cdr.detectChanges();
  }

  public $cancel(): void {
    console.log("$cancel() — cancelChat observers:", this.cancelChat.observers?.length ?? 'N/A');
    this.cancelChat.emit();
  }
}
